import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Card, Alert, Spinner, Badge, OverlayTrigger, Tooltip, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';

const DOMAIN_LIST = [
  "PROJECT COORDINATOR",
  "HUMAN RESOURCE COORDINATOR",
  "REACT DEVELOPER INTERN",
  "AI TRAINER INTERN",
  "NEXT.JS INTERN",
  "CREATIVE CONTENT WRITER INTERN",
  "LEGAL CONTENT WRITER INTERN",
  "PHP DEVELOPER INTERN (Pan)",
  "PHP DEVELOPER INTERN (Pune)",
  "DEVOPS INTERN",
  "ACCOUNTS AND ADMIN",
  "EMBEDDED INTERN",
  "AI DEVELOPER",
  "EVENT MANAGER",
  "TELECALLER",
  "SOFTWARE TESTING",
  "SCRIPT WRITER",
  "VIDEO EDITOR",
  "SHOW ANCHOR",
  "N8N DEVELOPMENT INTERN",
  ".NET DEVELOPER",
  "UI UX INTERN",
  "IOS APP DEVELOPMENT INTERN",
  "FLUTTER DEVELOPER",
  "JAVA DEVELOPERS INTERNS"
];

const EMAIL_TEMPLATES = {
  meet_greet: {
    name: 'Meet & Greet Invitation',
    subject: 'Invitation: Meet and Greet Session - Internship Opportunity',
    body: `Dear {first_name},

Thank you for showing interest in our internship opportunity. We are delighted to invite you to a "Meet and Greet" session, where we look forward to learning more about you and sharing further details about the opportunity.

Session Details:
📅 Date: {meet_greet_date}
⏰ Time: 07:30 PM – 08:30 PM
🔗 Zoom Link: https://us06web.zoom.us/j/87637708173?pwd=1bPAkFMcWheu2GGTIiTlxEHjPATSGy.1

Action Required:
Kindly share your updated CV with us at 📧 mandard@smartcookie.in before the session.

For any queries or assistance regarding the meeting, feel free to contact us at 📞 6203057899

More About the Projects:

Smart Rewards: https://drive.google.com/file/d/1RfDjdPP0JRryE_FS2Iq59BNeLnAZ05zN/view?usp=sharing

Continuous Job Network (CJN): https://drive.google.com/file/d/1RgxlOnB4xmBMnfKj4ZjP56f4QfiOEJEK/view?usp=sharing

We look forward to connecting with you and discussing how you can be part of our exciting journey.

Warm regards,
Khushpreet Kaur
HR Team
Blue Planet Solutions`
  },
  follow_up: {
    name: 'Follow-up Reminder',
    subject: 'Reminder: Meet and Greet Session for {domain} - {ats_number}',
    body: `Dear {first_name},

This is a friendly reminder about the upcoming Meet and Greet session for the {domain} position.

We noticed you haven't responded to our previous invitation. We'd love to have you join us!

Session Details:
📅 Date: {meet_greet_date}
⏰ Time: 07:30 PM – 08:30 PM
🔗 Zoom Link: https://us06web.zoom.us/j/87637708173?pwd=1bPAkFMcWheu2GGTIiTlxEHjPATSGy.1

Please confirm your attendance by replying to this email.

Best regards,
HR Team
Blue Planet Solutions`
  },
  custom: {
    name: 'Custom Template',
    subject: '',
    body: ''
  }
};

const DomainSummaryReportCard = ({ startDate: propStartDate, endDate: propEndDate }) => {
  const [data, setData] = useState([]);
  const [emailLogData, setEmailLogData] = useState({});
  const [startDate, setStartDate] = useState(propStartDate);
  const [endDate, setEndDate] = useState(propEndDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  // Email Modal States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [domainApplicants, setDomainApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Email Log Modal
  const [showEmailLogModal, setShowEmailLogModal] = useState(false);
  const [emailLogStats, setEmailLogStats] = useState(null);
  const [emailLogDetails, setEmailLogDetails] = useState([]);
  const [loadingLogDetails, setLoadingLogDetails] = useState(false);

  // Template Management
  const [selectedTemplate, setSelectedTemplate] = useState('meet_greet');
  const [emailTemplate, setEmailTemplate] = useState(EMAIL_TEMPLATES.meet_greet);
  const [customPlaceholders, setCustomPlaceholders] = useState({
    meet_greet_date: '27th June 2025'
  });

  // Email History
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedApplicantHistory, setSelectedApplicantHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    setStartDate(propStartDate);
    setEndDate(propEndDate);
  }, [propStartDate, propEndDate]);

  const fetchDomainSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${base}/api/applicants/reports/domain-summary?start_date=${startDate}&end_date=${endDate}`;
      const res = await axios.get(url, { withCredentials: true });
      setData(res.data.data || []);
      await fetchAllEmailLogs();
    } catch (err) {
      console.error('Error fetching domain summary:', err);
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmailLogs = async () => {
    try {
      setEmailLogLoading(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const logData = {};

      for (const domain of DOMAIN_LIST) {
        try {
          const url = `${base}/api/applicants/reports/domain-email-log?domain=${encodeURIComponent(domain)}&start_date=${startDate}&end_date=${endDate}`;
          const res = await axios.get(url, { withCredentials: true });
          logData[domain] = res.data.stats;
        } catch (err) {
          console.error(`Error fetching email log for ${domain}:`, err);
          logData[domain] = {
            total_applicants: 0,
            total_emails_sent: 0,
            total_emails_skipped: 0,
            applicants_contacted: 0,
            last_email_sent: null
          };
        }
      }

      setEmailLogData(logData);
    } catch (err) {
      console.error('Error in fetchAllEmailLogs:', err);
    } finally {
      setEmailLogLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchDomainSummary();
    }
  }, [startDate, endDate]);

  const fetchDomainApplicants = async (domain) => {
    try {
      setLoadingApplicants(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${base}/api/applicants/reports/domain-applicants?domain=${encodeURIComponent(domain)}&start_date=${startDate}&end_date=${endDate}`;
      const res = await axios.get(url, { withCredentials: true });
      const applicants = res.data.applicants || [];
      setDomainApplicants(applicants);
      setSelectedApplicants(applicants.map(a => a.ats_number));
      return applicants;
    } catch (err) {
      console.error('Error fetching domain applicants:', err);
      toast.error('Failed to load applicants for this domain');
      return [];
    } finally {
      setLoadingApplicants(false);
    }
  };

  const fetchEmailHistory = async (atsNumber) => {
    try {
      setLoadingHistory(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${base}/api/applicants/notifications/email-history/${atsNumber}`;
      const res = await axios.get(url, { withCredentials: true });
      return res.data.history || [];
    } catch (err) {
      console.error('Error fetching email history:', err);
      toast.error('Failed to load email history');
      return [];
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSendEmailClick = async (domain) => {
    setSelectedDomain(domain);
    setShowEmailModal(true);
    await fetchDomainApplicants(domain);
  };

  const handleViewEmailLog = async (domain) => {
    setSelectedDomain(domain);
    const stats = emailLogData[domain];
    setEmailLogStats(stats);
    setShowEmailLogModal(true);
    await fetchEmailLogDetails(domain);
  };

  const fetchEmailLogDetails = async (domain) => {
    try {
      setLoadingLogDetails(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${base}/api/applicants/reports/email-logs?domain=${encodeURIComponent(domain)}&start_date=${startDate}&end_date=${endDate}&limit=100`;
      const res = await axios.get(url, { withCredentials: true });
      setEmailLogDetails(res.data.data || []);
    } catch (err) {
      console.error('Error fetching email log details:', err);
      setEmailLogDetails([]);
    } finally {
      setLoadingLogDetails(false);
    }
  };

  const handleViewHistory = async (applicant) => {
    setSelectedApplicantHistory(applicant);
    setShowHistoryModal(true);
    const history = await fetchEmailHistory(applicant.ats_number);
    setSelectedApplicantHistory({ ...applicant, history });
  };

  const handleTemplateChange = (templateKey) => {
    setSelectedTemplate(templateKey);
    setEmailTemplate(EMAIL_TEMPLATES[templateKey]);
  };

  const handleSelectApplicant = (atsNumber) => {
    setSelectedApplicants(prev =>
      prev.includes(atsNumber)
        ? prev.filter(num => num !== atsNumber)
        : [...prev, atsNumber]
    );
  };

  const handleSelectAll = () => {
    if (selectedApplicants.length === domainApplicants.length) {
      setSelectedApplicants([]);
    } else {
      setSelectedApplicants(domainApplicants.map(a => a.ats_number));
    }
  };

  const replacePlaceholders = (text, applicant) => {
    let result = text;
    result = result.replace(/{first_name}/g, applicant.first_name || '');
    result = result.replace(/{last_name}/g, applicant.last_name || '');
    result = result.replace(/{ats_number}/g, applicant.ats_number || '');
    result = result.replace(/{domain}/g, selectedDomain || '');
    result = result.replace(/{email}/g, applicant.email_id || '');
    
    // Custom placeholders
    Object.keys(customPlaceholders).forEach(key => {
      result = result.replace(new RegExp(`{${key}}`, 'g'), customPlaceholders[key]);
    });
    
    return result;
  };

  const sendDomainEmails = async () => {
    if (sendingEmail) return;

    try {
      if (selectedApplicants.length === 0) {
        toast.error('Please select at least one applicant to send emails to');
        return;
      }

      const confirmMessage = `Are you sure you want to send emails to ${selectedApplicants.length} applicant(s)?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      setSendingEmail(true);
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

      const response = await axios.post(`${base}/api/applicants/notifications/send-reminders`, {
        atsNumbers: selectedApplicants,
        subject: emailTemplate.subject,
        body: emailTemplate.body,
        sentVia: 'domain_summary',
        customPlaceholders
      }, { withCredentials: true });

      const { summary, details } = response.data;

      if (summary.successful > 0) {
        toast.success(`✅ Successfully sent ${summary.successful} email(s) to ${selectedDomain}`);
      }

      if (summary.failed > 0) {
        toast.error(`❌ Failed: ${summary.failed} email(s)`);
        console.error('Failed emails:', details.failed);
      }

      if (summary.skipped > 0) {
        toast.warning(`⚠️ Skipped ${summary.skipped} invalid email(s)`);
        console.warn('Skipped emails:', details.skipped);
      }

      setShowEmailModal(false);
      setDomainApplicants([]);
      setSelectedApplicants([]);
      setSelectedDomain('');

      await fetchAllEmailLogs();
    } catch (error) {
      console.error('Error sending emails:', error);
      if (error.response?.data?.summary) {
        const { summary } = error.response.data;
        toast.error(`Failed: ${summary.failed || 0}, Skipped: ${summary.skipped || 0}`);
      } else {
        toast.error('Failed to send emails. Please try again.');
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const { transposedData, dateColumns } = useMemo(() => {
    if (!data.length) return { transposedData: [], dateColumns: [] };

    const domainMap = new Map(DOMAIN_LIST.map(domain => [domain.toUpperCase().trim(), domain]));
    const grouped = {};
    const allDates = new Set();

    data.forEach(item => {
      if (!item?.domain || !item?.date) return;

      const date = item.date.split('T')[0];
      allDates.add(date);

      const rawDomain = item.domain.trim();
      const domainKey = rawDomain.toUpperCase();
      let matchedDomain = domainMap.get(domainKey);

      if (!matchedDomain) {
        matchedDomain = DOMAIN_LIST.find(dom =>
          dom.toUpperCase().includes(domainKey) || domainKey.includes(dom.toUpperCase())
        );
      }

      if (!matchedDomain) {
        console.warn(`Domain not found: "${rawDomain}"`);
        return;
      }

      if (!grouped[matchedDomain]) grouped[matchedDomain] = {};
      grouped[matchedDomain][date] = (grouped[matchedDomain][date] || 0) + (item.count || 0);
    });

    const sortedDates = Array.from(allDates).sort();

    const rows = DOMAIN_LIST.map(domain => {
      const row = { domain };
      let rowTotal = 0;

      sortedDates.forEach(date => {
        const count = grouped[domain]?.[date] || 0;
        row[date] = count;
        rowTotal += count;
      });

      row.total = rowTotal;
      return row;
    });

    return { transposedData: rows, dateColumns: sortedDates };
  }, [data]);

  const formatDateUS = (dateStr) => {
    const d = new Date(dateStr);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}/${month}/${day}`;
  };

  const formatDateShort = (dateStr) => {
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const year = d.getFullYear();
    return `${year}/${months[d.getMonth()]} ${day}`;
  };

  const exportToCSV = () => {
    if (!transposedData.length) return;
    const headers = ['S.No', 'Domain', 'Total', 'Emails Sent', 'Emails Skipped', ...dateColumns.map(formatDateUS)];
    const csvRows = [headers.join(',')];

    transposedData.forEach((row, index) => {
      const emailLog = emailLogData[row.domain];
      const rowData = [
        index + 1,
        `"${row.domain}"`,
        row.total,
        emailLog?.total_emails_sent || 0,
        emailLog?.total_emails_skipped || 0
      ];
      dateColumns.forEach(date => {
        rowData.push(row[date] || 0);
      });
      csvRows.push(rowData.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domain_summary_${startDate}_to_${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderEmailLogTooltip = (domain) => {
    const stats = emailLogData[domain];
    if (!stats) return <Tooltip>Loading...</Tooltip>;

    return (
      <Tooltip>
        <div style={{ textAlign: 'left' }}>
          <strong>Email Statistics:</strong><br />
          Total Sent: {stats.total_emails_sent || 0}<br />
          Skipped: {stats.total_emails_skipped || 0}<br />
          Contacted: {stats.applicants_contacted || 0}/{stats.total_applicants || 0}<br />
          {stats.last_email_sent && (
            <>Last Sent: {new Date(stats.last_email_sent).toLocaleString()}</>
          )}
        </div>
      </Tooltip>
    );
  };

  const getStatusBadge = (emailSentCount, emailSkipCount) => {
    if (emailSentCount > 0 && emailSkipCount === 0) {
      return <Badge bg="success"><i className="bi bi-check-circle-fill me-1"></i>All Sent</Badge>;
    }
    if (emailSentCount > 0 && emailSkipCount > 0) {
      return <Badge bg="warning"><i className="bi bi-exclamation-triangle-fill me-1"></i>Partial</Badge>;
    }
    if (emailSentCount === 0 && emailSkipCount > 0) {
      return <Badge bg="danger"><i className="bi bi-x-circle-fill me-1"></i>All Skipped</Badge>;
    }
    return <Badge bg="secondary"><i className="bi bi-dash-circle me-1"></i>No Emails</Badge>;
  };

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '1rem' }}>
      <style>{`
        .domain-report-card {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        .filter-card {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid #e5e7eb;
        }
        .filter-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .filter-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .filter-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        .form-group-custom label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        .form-group-custom label i {
          color: #6366f1;
        }
        .form-control-custom {
          width: 100%;
          padding: 0.625rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          transition: all 0.2s;
          background: #ffffff;
        }
        .form-control-custom:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        .btn-custom {
          width: 100%;
          padding: 0.625rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        .btn-primary-custom {
          background: #6366f1;
          color: white;
        }
        .btn-primary-custom:hover:not(:disabled) {
          background: #4f46e5;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(99, 102, 241, 0.25);
        }
        .btn-success-custom {
          background: #10b981;
          color: white;
        }
        .btn-success-custom:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(16, 185, 129, 0.25);
        }
        .btn-custom:disabled {
          background: #d1d5db;
          cursor: not-allowed;
          opacity: 0.6;
        }
        .table-card {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }
        .table-container {
          max-height: 600px;
          overflow-y: auto;
          overflow-x: auto;
        }
        .custom-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .custom-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .custom-table th {
          padding: 0.875rem;
          text-align: center;
          font-weight: 600;
          border-right: 1px solid rgba(255,255,255,0.15);
          white-space: nowrap;
        }
        .custom-table th.total-col {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }
        .custom-table th.email-log-col {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }
        .custom-table th.action-col {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .custom-table tbody tr {
          border-bottom: 1px solid #e5e7eb;
          transition: all 0.2s;
        }
        .custom-table tbody tr:hover {
          background: #f9fafb;
        }
        .custom-table tbody tr.empty-row {
          background: #fafafa;
        }
        .custom-table td {
          padding: 0.875rem;
          text-align: center;
          border-right: 1px solid #e5e7eb;
        }
        .custom-table td.sno-col {
          font-weight: 600;
          color: #6b7280;
        }
        .custom-table td.domain-col {
          text-align: left;
          font-weight: 600;
          color: #1f2937;
        }
        .custom-table td.total-col {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          font-weight: 700;
          font-size: 1.05em;
          color: #92400e;
        }
        .custom-table td.data-cell.has-data {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #065f46;
          font-weight: 600;
        }
        .custom-table td.data-cell.no-data {
          color: #d1d5db;
        }
        .email-log-badge {
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          gap: 0.25rem;
          align-items: center;
        }
        .email-log-badge:hover {
          transform: scale(1.05);
        }
        .loading-container {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          padding: 3rem;
          text-align: center;
          border: 1px solid #e5e7eb;
        }
        .spinner {
          display: inline-block;
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .empty-state {
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          border: 1px solid #c4b5fd;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
        }
        .modal-custom .modal-content {
          border-radius: 12px;
          border: none;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .modal-custom .modal-header {
          border-bottom: 1px solid #e5e7eb;
          padding: 1.25rem 1.5rem;
          background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
        }
        .email-log-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin: 1rem 0;
        }
        .stat-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          text-align: center;
        }
        .stat-card h3 {
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
        }
        .stat-card p {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.25rem 0 0 0;
        }
        .applicant-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          transition: all 0.2s;
          cursor: pointer;
        }
        .applicant-card:hover {
          border-color: #6366f1;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
        }
        .applicant-card.selected {
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          border-color: #6366f1;
        }
        .timeline-item {
          position: relative;
          padding-left: 2rem;
          padding-bottom: 1.5rem;
          border-left: 2px solid #e5e7eb;
        }
        .timeline-item:last-child {
          border-left-color: transparent;
        }
        .timeline-dot {
          position: absolute;
          left: -6px;
          top: 0;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #6366f1;
          border: 2px solid #ffffff;
        }
        .timeline-dot.success {
          background: #10b981;
        }
        .timeline-dot.failed {
          background: #ef4444;
        }
        .timeline-dot.skipped {
          background: #f59e0b;
        }
      `}</style>

      <div className="domain-report-card">
        {/* Filters Section */}
        <div className="filter-card">
          <div className="filter-grid">
            <div className="form-group-custom">
              <label>
                <i className="bi bi-calendar3"></i>
                From Date
              </label>
              <input
                type="date"
                className="form-control-custom"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="form-group-custom">
              <label>
                <i className="bi bi-calendar3"></i>
                To Date
              </label>
              <input
                type="date"
                className="form-control-custom"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={fetchDomainSummary} className="btn-custom btn-primary-custom" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search"></i>
                    Apply
                  </>
                )}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={exportToCSV}
                disabled={!transposedData.length}
                className="btn-custom btn-success-custom"
              >
                <i className="bi bi-download"></i>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', color: '#6b7280', fontWeight: 600 }}>Loading data...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {/* Table Section */}
        {!loading && !error && transposedData.length > 0 && (
          <div className="table-card">
            {emailLogLoading && (
              <Alert variant="info" style={{ margin: '1rem', borderRadius: '8px' }}>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading email statistics...
              </Alert>
            )}
            <div className="table-container">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: '60px' }}>S.No</th>
                    <th style={{ minWidth: '250px', textAlign: 'left' }}>Domain</th>
                    <th className="total-col" style={{ minWidth: '100px' }}>Total</th>
                    <th className="email-log-col" style={{ minWidth: '150px' }}>
                      <i className="bi bi-envelope-check me-1"></i>
                      Email Log
                    </th>
                    <th className="action-col" style={{ minWidth: '120px' }}>Actions</th>
                    {dateColumns.map((date, idx) => (
                      <th key={idx} style={{ minWidth: '100px' }}>
                        <div>{formatDateShort(date)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transposedData.map((row, i) => {
                    const emailLog = emailLogData[row.domain];
                    const hasEmailData = emailLog && (emailLog.total_emails_sent > 0 || emailLog.total_emails_skipped > 0);

                    return (
                      <tr key={i} className={row.total === 0 ? 'empty-row' : ''}>
                        <td className="sno-col">{i + 1}</td>
                        <td className="domain-col" style={{ color: row.total === 0 ? '#9ca3af' : '#1f2937' }}>
                          {row.domain}
                        </td>
                        <td className="total-col">{row.total}</td>
                        <td>
                          {emailLogLoading ? (
                            <Spinner animation="border" size="sm" variant="secondary" />
                          ) : hasEmailData ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={renderEmailLogTooltip(row.domain)}
                            >
                              <div onClick={() => handleViewEmailLog(row.domain)} className="email-log-badge">
                                <Badge bg="info" className="me-1">
                                  <i className="bi bi-send-check-fill me-1"></i>
                                  {emailLog.total_emails_sent || 0}
                                </Badge>
                                {emailLog.total_emails_skipped > 0 && (
                                  <Badge bg="warning">
                                    <i className="bi bi-x-circle-fill me-1"></i>
                                    {emailLog.total_emails_skipped}
                                  </Badge>
                                )}
                              </div>
                            </OverlayTrigger>
                          ) : (
                            <Badge bg="secondary">
                              <i className="bi bi-dash-circle me-1"></i>
                              No emails
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={row.total === 0}
                            onClick={() => handleSendEmailClick(row.domain)}
                            style={{ fontSize: '0.8rem' }}
                          >
                            <i className="bi bi-envelope-fill me-1"></i>
                            Send
                          </Button>
                        </td>
                        {dateColumns.map((date, j) => (
                          <td
                            key={j}
                            className={`data-cell ${row[date] > 0 ? 'has-data' : 'no-data'}`}
                          >
                            {row[date] || '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && transposedData.length === 0 && (
          <div className="empty-state">
            <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
            <p>No applications found for the selected date range.</p>
          </div>
        )}

        {/* Enhanced Email Modal */}
        <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="xl" className="modal-custom">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-envelope-paper me-2"></i>
              Send Email - {selectedDomain}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs defaultActiveKey="recipients" className="mb-3">
              {/* Recipients Tab */}
              <Tab eventKey="recipients" title={<span><i className="bi bi-people me-2"></i>Recipients ({selectedApplicants.length})</span>}>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-0">Select Applicants</h6>
                      <small className="text-muted">
                        {selectedApplicants.length} of {domainApplicants.length} selected
                      </small>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedApplicants.length === domainApplicants.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {loadingApplicants ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3 text-muted">Loading applicants...</p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {domainApplicants.length > 0 ? (
                        domainApplicants.map(applicant => (
                          <div
                            key={applicant.ats_number}
                            className={`applicant-card ${selectedApplicants.includes(applicant.ats_number) ? 'selected' : ''}`}
                            onClick={() => handleSelectApplicant(applicant.ats_number)}
                          >
                            <div className="d-flex align-items-start">
                              <Form.Check
                                type="checkbox"
                                checked={selectedApplicants.includes(applicant.ats_number)}
                                onChange={() => {}}
                                className="me-3"
                              />
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="mb-1">
                                      {applicant.first_name} {applicant.last_name}
                                    </h6>
                                    <div className="text-muted small">
                                      <i className="bi bi-envelope me-1"></i>
                                      {applicant.email_id}
                                    </div>
                                  </div>
                                  <Badge bg="secondary" className="ms-2">
                                    {applicant.ats_number}
                                  </Badge>
                                </div>

                                {applicant.email_sent_count > 0 && (
                                  <div className="d-flex gap-2 mt-2">
                                    <Badge bg="info">
                                      <i className="bi bi-send-check me-1"></i>
                                      Sent: {applicant.email_sent_count}
                                    </Badge>
                                    {applicant.email_skip_count > 0 && (
                                      <Badge bg="warning">
                                        <i className="bi bi-x-circle me-1"></i>
                                        Skipped: {applicant.email_skip_count}
                                      </Badge>
                                    )}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 ms-auto"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewHistory(applicant);
                                      }}
                                    >
                                      <i className="bi bi-clock-history me-1"></i>
                                      View History
                                    </Button>
                                  </div>
                                )}

                                {applicant.last_email_sent_at && (
                                  <div className="text-muted small mt-1">
                                    <i className="bi bi-clock me-1"></i>
                                    Last sent: {new Date(applicant.last_email_sent_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <Alert variant="warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          No eligible applicants found for this domain.
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              </Tab>

              {/* Template Tab */}
              <Tab eventKey="template" title={<span><i className="bi bi-file-text me-2"></i>Email Template</span>}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Template</Form.Label>
                  <Form.Select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    <option value="meet_greet">Meet & Greet Invitation</option>
                    <option value="follow_up">Follow-up Reminder</option>
                    <option value="custom">Custom Template</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={emailTemplate.subject}
                    onChange={e => setEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Enter email subject"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={emailTemplate.body}
                    onChange={e => setEmailTemplate(prev => ({ ...prev, body: e.target.value }))}
                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    placeholder="Enter email body"
                  />
                  <Form.Text className="text-muted">
                    Available placeholders: {'{first_name}'}, {'{last_name}'}, {'{ats_number}'}, {'{domain}'}, {'{email}'}, {'{meet_greet_date}'}
                  </Form.Text>
                </Form.Group>

                <Card bg="light" className="mb-3">
                  <Card.Header>
                    <strong>Custom Placeholders</strong>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>Meet & Greet Date</Form.Label>
                      <Form.Control
                        type="text"
                        value={customPlaceholders.meet_greet_date}
                        onChange={e => setCustomPlaceholders(prev => ({ ...prev, meet_greet_date: e.target.value }))}
                        placeholder="e.g., 27th June 2025"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Tab>

              {/* Preview Tab */}
              <Tab eventKey="preview" title={<span><i className="bi bi-eye me-2"></i>Preview</span>}>
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  This is a preview of how the email will look for the first selected applicant.
                </Alert>

                {selectedApplicants.length > 0 && domainApplicants.length > 0 && (
                  <>
                    <Card className="mb-3">
                      <Card.Header>
                        <strong>Preview Recipient</strong>
                      </Card.Header>
                      <Card.Body>
                        {(() => {
                          const previewApplicant = domainApplicants.find(a => a.ats_number === selectedApplicants[0]);
                          return previewApplicant ? (
                            <div>
                              <p className="mb-1">
                                <strong>Name:</strong> {previewApplicant.first_name} {previewApplicant.last_name}
                              </p>
                              <p className="mb-1">
                                <strong>Email:</strong> {previewApplicant.email_id}
                              </p>
                              <p className="mb-0">
                                <strong>ATS Number:</strong> {previewApplicant.ats_number}
                              </p>
                            </div>
                          ) : null;
                        })()}
                      </Card.Body>
                    </Card>

                    <Card className="mb-3">
                      <Card.Header>
                        <strong>Subject</strong>
                      </Card.Header>
                      <Card.Body>
                        {(() => {
                          const previewApplicant = domainApplicants.find(a => a.ats_number === selectedApplicants[0]);
                          return previewApplicant ? replacePlaceholders(emailTemplate.subject, previewApplicant) : '';
                        })()}
                      </Card.Body>
                    </Card>

                    <Card>
                      <Card.Header>
                        <strong>Body</strong>
                      </Card.Header>
                      <Card.Body>
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                          {(() => {
                            const previewApplicant = domainApplicants.find(a => a.ats_number === selectedApplicants[0]);
                            return previewApplicant ? replacePlaceholders(emailTemplate.body, previewApplicant) : '';
                          })()}
                        </pre>
                      </Card.Body>
                    </Card>
                  </>
                )}

                {selectedApplicants.length === 0 && (
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Please select at least one applicant to preview the email.
                  </Alert>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <div className="text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                {selectedApplicants.length} applicant(s) will receive this email
              </div>
              <div>
                <Button variant="secondary" onClick={() => setShowEmailModal(false)} className="me-2">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={sendDomainEmails}
                  disabled={sendingEmail || selectedApplicants.length === 0 || loadingApplicants}
                >
                  {sendingEmail ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Send to {selectedApplicants.length} Applicant{selectedApplicants.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Email Log Modal */}
        <Modal show={showEmailLogModal} onHide={() => setShowEmailLogModal(false)} size="lg" className="modal-custom">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-envelope-paper me-2"></i>
              Email Log - {selectedDomain}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {emailLogStats ? (
              <>
                <div className="email-log-stats">
                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#93c5fd' }}>
                    <h3 style={{ color: '#1e40af' }}>{emailLogStats.total_applicants || 0}</h3>
                    <p style={{ color: '#1e3a8a' }}>Total Applicants</p>
                  </div>

                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', borderColor: '#6ee7b7' }}>
                    <h3 style={{ color: '#065f46' }}>{emailLogStats.total_emails_sent || 0}</h3>
                    <p style={{ color: '#064e3b' }}>Emails Sent</p>
                  </div>

                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderColor: '#fcd34d' }}>
                    <h3 style={{ color: '#92400e' }}>{emailLogStats.total_emails_skipped || 0}</h3>
                    <p style={{ color: '#78350f' }}>Emails Skipped</p>
                  </div>

                  <div className="stat-card" style={{ background: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)', borderColor: '#c084fc' }}>
                    <h3 style={{ color: '#6b21a8' }}>{emailLogStats.applicants_contacted || 0}</h3>
                    <p style={{ color: '#581c87' }}>Applicants Contacted</p>
                  </div>
                </div>

                {emailLogStats.last_email_sent && (
                  <Alert variant="info" className="mt-3">
                    <i className="bi bi-clock-history me-2"></i>
                    <strong>Last Email Sent:</strong> {new Date(emailLogStats.last_email_sent).toLocaleString()}
                  </Alert>
                )}

                <div className="mt-4">
                  <h6 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Summary</h6>
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem' }}>
                    <Row>
                      <Col md={4}>
                        <div className="text-center p-2">
                          <div className="text-muted small">Success Rate</div>
                          <div className="h4 mb-0 text-success">
                            {emailLogStats.total_applicants > 0
                              ? `${Math.round((emailLogStats.applicants_contacted / emailLogStats.total_applicants) * 100)}%`
                              : 'N/A'}
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center p-2">
                          <div className="text-muted small">Avg Emails/Applicant</div>
                          <div className="h4 mb-0">
                            {emailLogStats.applicants_contacted > 0
                              ? (emailLogStats.total_emails_sent / emailLogStats.applicants_contacted).toFixed(1)
                              : '0'}
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center p-2">
                          <div className="text-muted small">Skip Rate</div>
                          <div className="h4 mb-0 text-warning">
                            {(emailLogStats.total_emails_sent + emailLogStats.total_emails_skipped) > 0
                              ? `${Math.round((emailLogStats.total_emails_skipped / (emailLogStats.total_emails_sent + emailLogStats.total_emails_skipped)) * 100)}%`
                              : '0%'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="mt-4">
                  <h6 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Recent Activity</h6>
                  {loadingLogDetails ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" />
                    </div>
                  ) : emailLogDetails.length > 0 ? (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>ATS</th>
                            <th>Email</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailLogDetails.map(log => (
                            <tr key={log.id}>
                              <td className="small">{new Date(log.created_at).toLocaleString()}</td>
                              <td><Badge bg="secondary">{log.ats_number}</Badge></td>
                              <td className="small">{log.email}</td>
                              <td>
                                {log.status === 'sent' && <Badge bg="success">Sent</Badge>}
                                {log.status === 'failed' && <Badge bg="danger">Failed</Badge>}
                                {log.status === 'skipped' && <Badge bg="warning">Skipped</Badge>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <Alert variant="secondary">No recent activity</Alert>
                  )}
                </div>

                <Alert variant="secondary" className="mt-3 mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>
                    Statistics for <strong>{selectedDomain}</strong> from{' '}
                    <strong>{formatDateUS(startDate)}</strong> to <strong>{formatDateUS(endDate)}</strong>
                  </small>
                </Alert>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <Spinner animation="border" variant="primary" />
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading email statistics...</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailLogModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Email History Modal */}
        <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-clock-history me-2"></i>
              Email History - {selectedApplicantHistory?.first_name} {selectedApplicantHistory?.last_name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingHistory ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3 text-muted">Loading history...</p>
              </div>
            ) : selectedApplicantHistory?.history?.length > 0 ? (
              <>
                <div className="mb-3">
                  <Row>
                    <Col md={6}>
                      <p className="mb-1"><strong>ATS Number:</strong> {selectedApplicantHistory.ats_number}</p>
                      <p className="mb-1"><strong>Email:</strong> {selectedApplicantHistory.email_id}</p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>Total Sent:</strong> <Badge bg="success">{selectedApplicantHistory.email_sent_count || 0}</Badge></p>
                      <p className="mb-1"><strong>Total Skipped:</strong> <Badge bg="warning">{selectedApplicantHistory.email_skip_count || 0}</Badge></p>
                    </Col>
                  </Row>
                </div>

                <hr />

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {selectedApplicantHistory.history.map((item, index) => (
                    <div key={item.id || index} className="timeline-item">
                      <div className={`timeline-dot ${item.status || 'success'}`}></div>
                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div>
                            <strong className="small">{item.subject || 'No Subject'}</strong>
                            {item.status === 'sent' && <Badge bg="success" className="ms-2">Sent</Badge>}
                            {item.status === 'failed' && <Badge bg="danger" className="ms-2">Failed</Badge>}
                            {item.status === 'skipped' && <Badge bg="warning" className="ms-2">Skipped</Badge>}
                          </div>
                          <span className="text-muted small">
                            {new Date(item.sent_at || item.created_at).toLocaleString()}
                          </span>
                        </div>
                        {item.error_message && (
                          <Alert variant="danger" className="small mb-2 py-1">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            {item.error_message}
                          </Alert>
                        )}
                        {item.sent_via && (
                          <div className="text-muted small">
                            <i className="bi bi-tag me-1"></i>
                            Via: {item.sent_via}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Alert variant="info">
                <i className="bi bi-info-circle me-2"></i>
                No email history found for this applicant.
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default DomainSummaryReportCard;